import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, formatApiErrorDetail } from '../contexts/AuthContext';
import { KeyRound, User, Lock, Eye, EyeOff, ArrowRight } from 'lucide-react';
import { BrandSplash, BrandWordmark, BRAND_GRADIENT } from '../components/BrandSplash';

const field = 'flex items-center h-14 rounded-lg border border-slate-200 bg-white focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100 transition-colors';
const input = 'flex-1 h-full bg-transparent outline-none text-base text-slate-800 placeholder:text-slate-400';

function SsoError() {
  try {
    const u = new URL(window.location.href);
    if (u.searchParams.get('sso_error') !== '1') return null;
    const reason = u.searchParams.get('sso_reason') || '';
    return (
      <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-2 rounded-lg text-sm mb-4" data-testid="sso-error-banner">
        <div className="font-bold mb-1">Swami Hub sign-in failed</div>
        {reason && <div className="text-xs font-mono bg-white/60 rounded px-2 py-1 mb-1 break-all">{reason}</div>}
        <div className="text-xs">Go back to the Hub and click the tile again, or sign in manually below.</div>
      </div>
    );
  } catch { return null; }
}

export default function Login() {
  const [showPasskey, setShowPasskey] = useState(false);
  const [passkey, setPasskey] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [splash, setSplash] = useState(2);
  const { login, passkeyLogin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const t1 = setTimeout(() => setSplash(1), 2700);
    const t2 = setTimeout(() => setSplash(0), 3200);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      const result = await login(username, password);
      navigate(result.role === 'master' ? '/master' : '/dashboard');
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); } finally { setLoading(false); }
  };

  const handlePasskeySubmit = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      const result = await passkeyLogin(passkey);
      if (result.status === 'register') navigate('/register', { state: { passkey_type: result.passkey_type, company_id: result.company_id, passkey_id: result.passkey_id } });
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); } finally { setLoading(false); }
  };

  const submitBtn = (label, testid) => (
    <button type="submit" disabled={loading} style={{ background: BRAND_GRADIENT }} data-testid={testid}
      className="w-full h-14 rounded-lg text-white text-lg font-bold flex items-center justify-center gap-2 shadow-md hover:brightness-110 active:scale-[0.99] transition-[filter,transform] disabled:opacity-70">
      {loading ? 'Please wait...' : <>{label} <ArrowRight className="h-5 w-5" /></>}
    </button>
  );

  return (
    <>
      {splash > 0 && <BrandSplash fading={splash === 1} />}
      <div className="relative min-h-screen bg-gradient-to-b from-white to-slate-50 overflow-hidden flex items-center justify-center px-4 py-10" data-testid="login-page">
        <img src="/login-left.jpg" alt="" style={{ WebkitMaskImage: 'linear-gradient(to right, black 60%, transparent)', maskImage: 'linear-gradient(to right, black 60%, transparent)' }} className="hidden md:block absolute left-0 top-0 h-full w-auto max-w-[30vw] object-cover object-left pointer-events-none select-none" />
        <img src="/login-right.jpg" alt="" style={{ WebkitMaskImage: 'linear-gradient(to left, black 60%, transparent)', maskImage: 'linear-gradient(to left, black 60%, transparent)' }} className="hidden md:block absolute right-0 top-0 h-full w-auto max-w-[27vw] object-cover object-right pointer-events-none select-none" />
        <div className="relative w-full max-w-xl flex flex-col items-center">
          <BrandWordmark />
          <div className="mt-8 w-full bg-white/95 backdrop-blur rounded-2xl shadow-[0_20px_60px_-20px_rgba(15,23,42,0.25)] border border-slate-100 p-6 sm:p-10">
            {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg text-sm mb-4" data-testid="login-error">{error}</div>}
            <SsoError />
            {!showPasskey ? (
              <form onSubmit={handleLogin} className="space-y-4">
                <label className={field}><User className="h-6 w-6 mx-5 text-slate-500" />
                  <input value={username} onChange={e => setUsername(e.target.value)} required placeholder="Username" className={input} data-testid="login-username-input" autoComplete="username" />
                </label>
                <label className={field}><Lock className="h-6 w-6 mx-5 text-slate-500" />
                  <input type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} required placeholder="Password" className={input} data-testid="login-password-input" autoComplete="current-password" />
                  <button type="button" onClick={() => setShowPw(v => !v)} className="px-5 text-slate-500 hover:text-slate-800" aria-label="Show password" data-testid="login-toggle-password">
                    {showPw ? <Eye className="h-5 w-5" /> : <EyeOff className="h-5 w-5" />}
                  </button>
                </label>
                <div className="flex justify-end">
                  <button type="button" onClick={() => setForgot(v => !v)} className="text-sm font-semibold text-slate-700 hover:text-blue-700" data-testid="forgot-password-link">Forgot Password?</button>
                </div>
                {forgot && (
                  <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900" data-testid="forgot-password-message">
                    Please sign in through <b>Swami Hub (SSO)</b> and open Swami OEE from your Hub tile — no password needed. If you still can't get in, ask your company admin.
                  </div>
                )}
                {submitBtn('Login', 'login-submit-button')}
              </form>
            ) : (
              <form onSubmit={handlePasskeySubmit} className="space-y-4">
                <label className={field}><KeyRound className="h-6 w-6 mx-5 text-slate-500" />
                  <input value={passkey} onChange={e => setPasskey(e.target.value)} required placeholder="Enter your passkey" className={`${input} font-mono tracking-widest`} data-testid="passkey-input" />
                </label>
                {submitBtn('Activate Passkey', 'passkey-submit')}
              </form>
            )}
            <div className="mt-5 text-center">
              {!showPasskey ? (
                <button onClick={() => { setShowPasskey(true); setError(''); }} className="text-xs text-blue-600 hover:text-blue-800 font-medium inline-flex items-center gap-1" data-testid="show-passkey-link"><KeyRound className="h-3 w-3" /> Have a passkey? Click here</button>
              ) : (
                <button onClick={() => { setShowPasskey(false); setError(''); }} className="text-xs text-slate-500 hover:text-slate-700 font-medium" data-testid="back-to-login-link">Back to Sign In</button>
              )}
            </div>
          </div>
          <div className="mt-6 flex justify-center gap-4 text-[11px] text-slate-400" data-testid="login-legal-links">
            <a href="/privacy-policy.html" className="hover:text-slate-700 underline">Privacy Policy</a>
            <a href="/terms-of-service.html" className="hover:text-slate-700 underline">Terms of Service</a>
          </div>
        </div>
      </div>
    </>
  );
}
